import type React from "react";
import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { type GridColDef } from "@mui/x-data-grid";
import { AssignmentData, Venue } from "../types/site-visit";
import StatusTabs from "./StatusTabs";
import AssignSiteVisitModal from "./AssignSiteVisitModal";
import CustomTextField from "../../../components/ui/Textfield";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/Index";
import StatusCards from "./StatusCards";
import { DataGrid } from "../../../components/common/Index";

interface SiteVisitManagementProps {
  venues: Venue[];
  onAssignVenue: (siteVisitId: number, data: AssignmentData) => Promise<void>;
  onSendEmail: (userId: string, venueId: string) => Promise<void>;
  isLoading: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  currentStatus: string;
  onStatusChange: (status: string) => void;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  category: string;
  onCategoryChange: (category: string) => void;
}

const SiteVisitManagement: React.FC<SiteVisitManagementProps> = ({
  venues,
  onAssignVenue,
  onSendEmail,
  isLoading,
  totalItems,
  currentStatus,
  onStatusChange,
  onPageChange,
  onPageSizeChange,
  page,
  pageSize,
  onSearchTermChange,
  searchTerm,
  category: _category,
  onCategoryChange: _onCategoryChange,
}) => {
  void _category;
  void _onCategoryChange;
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  // Calculate status counts
  const statusCounts = useMemo(() => {
    return venues.reduce((acc, venue) => {
      acc[venue.status] = (acc[venue.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [venues]);

  const handleAssignClick = (venue: Venue) => {
    setSelectedVenue(venue);
    setModalOpen(true);
  };

  const handleAssign = async (siteVisitId: number, data: AssignmentData) => {
    setLoading(true);
    try {
      await onAssignVenue(siteVisitId, data);
      await onSendEmail(
        data.assigneeId,
        selectedVenue?.venueId?.toString() || ""
      ); // venue id is string
      setModalOpen(false);
      setSelectedVenue(null);
    } catch (error) {
      console.error("Error assigning venue:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    const statusColors = {
      pending: "#F79E1B40",
      assigned: "#0000FF26",
      awaiting_review: "#ff5722",
      approved: "#4caf50",
      rejected: "#f44336",
    };

    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        sx={{
          backgroundColor: statusColors[status as keyof typeof statusColors],
          color: reduxTheme.primary_colors[100],
          fontWeight: "medium",
        }}
      />
    );
  };

  const baseColumns: GridColDef[] = [
    {
      field: "name",
      headerName: "Venue Name",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", mt: 2, alignItems: "center", gap: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: "medium" }}>
            {params.row.name}
          </Typography>
        </Box>
      ),
    },
    {
      field: "address",
      headerName: "Address",
      flex: 1,
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
            {/* Venue on top */}
            <Typography variant="subtitle2" fontWeight="bold">
              {params.row.name || "No Venue"}
            </Typography>

            {/* Address below */}
            <Typography
              variant="body2"
              sx={{ whiteSpace: "normal", wordWrap: "break-word" }}
            >
              {params.row.address || "No Address"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: "Venue Type",
      width: 120,
      renderCell: (params) => getStatusChip(params.row.type || ""),
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => getStatusChip(params.row.status || ""),
    },
  ];

  const pendingColumns: GridColDef[] = [
    ...baseColumns,
    {
      field: "action",
      headerName: "Action",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => handleAssignClick(params.row)}
          sx={{
            backgroundColor: reduxTheme.primary_colors[400],
            "&:hover": { backgroundColor: "#A0522D" },
          }}
        >
          Assign
        </Button>
      ),
    },
  ];

  const assignedColumns: GridColDef[] = [
    ...baseColumns,
    { field: "assignee", headerName: "Assignee", flex: 1 },
    { field: "dueDate", headerName: "Scheduled Date", flex: 1 },
  ];

  return (
    <Box>
      <StatusCards />
      <StatusTabs
        currentStatus={currentStatus}
        onStatusChange={onStatusChange}
        statusCounts={statusCounts}
      />

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <CustomTextField
          placeholder="Search by venue name, type, or address..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => onSearchTermChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flexGrow: 1, maxWidth: 300 }}
        />
        {/* <CustomTextField
          placeholder="All Categories"
          variant="outlined"
          size="small"
          select
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          sx={{ minWidth: 50, flexGrow: 1, maxWidth: 150 }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          <MenuItem value="Chapel">Chapel</MenuItem>
          <MenuItem value="Funeral Home">Funeral Home</MenuItem>
          <MenuItem value="Cemetery">Cemetery</MenuItem>
        </CustomTextField> */}
      </Box>

      <DataGrid
        rows={venues}
        columns={
          currentStatus === "assigned" ? assignedColumns : pendingColumns
        }
        rowCount={totalItems}
        loading={isLoading}
        paginationMode="server"
        paginationModel={{ page: page, pageSize: pageSize }}
        onPaginationModelChange={(model) => {
          onPageChange(model.page);
          onPageSizeChange(model.pageSize);
        }}
        pageSizeOptions={[5, 10, 20]}
        rowHeight={60}
      />

      <AssignSiteVisitModal
        open={modalOpen}
        venue={selectedVenue}
        onClose={() => {
          setModalOpen(false);
          setSelectedVenue(null);
        }}
        onAssign={handleAssign}
        loading={loading}
      />
    </Box>
  );
};

export default SiteVisitManagement;
